import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUserClock,
    faTrash,
    faPencil
} from '@fortawesome/free-solid-svg-icons';
import { BaseFormViewModel } from '../common/ViewModel';
import SlideForm from '../common/forms/SlideForm';
import authService from '../api-authorization/AuthorizeService';
import { util } from '../Util';
import moment from 'moment';
import Select from 'react-select';
import {
    FormLabel,
    CollapseUnderlineHeader,
    FlexCenterRow,
    toasty
} from '../common/forms/FormElements';
import CommonContext, {
    ApiRoutes,
} from '../Common';
import {
    Button,
    FormGroup,
    Input,
    Badge
} from 'reactstrap';
import '../dailyDrivingLog/DailyDrivingLogForm.scss';
import TimesheetEdit from './TimesheetEdit';
import { TimesheetDetailsAdjustment, TimesheetChargeAdjustment } from '../timesheetManagement/TimesheetDetailsAdjustment';
import ValidatedSelect, {
    CompactSelectStyles,
} from '../common/forms/ValidatedSelect';
import NumericInput from '../common/forms/NumericInput';
import TimeEntry from '../common/forms/TimeEntry';

export default class TimesheetDetails extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.timesheetEditRef = React.createRef(); 

        const stateBase = {
            loading: false,
            saving: false,
            chargeTypes: [],
            ...new BaseFormViewModel(),
        };

        this.state = stateBase;
    }

    // #region METHODS

    componentDidMount() {
        this._subscription = authService.subscribe(() => this.populateState());
        //this.populateState();
    }

    componentWillUnmount() {
        return this.setState = (state, callback) => {

        };
    }

    open = async (date, employeeName, employeeId) => {

        const isAuthenticated = await authService.isAuthenticated();

        if (!isAuthenticated) {
            return;
        }

        this.props.toggleShow(true);
        this.context.setFormOpened(true);

        // Clear stale data and show loading overlay immediately so the
        // prior user's timesheet isn't visible while the new data loads.
        this.setState({
            loading: true,
            saving: false,
            dailyEntries: [],
            weeklyTotals: null,
            employee: null,
            employeeName: employeeName,
            employeeId: employeeId,
            date: date,
            chargesOpen: true,
            isAdjustingCharge: false,
        });

        const jobDate = moment(date).format('YYYYMMDD');

        let [dailyEntries, weeklyTotals, nonFlaggingContract, employee] = await Promise.all([
            util.fetch.js(ApiRoutes.dailyDrivingLog.byEmployee(jobDate, employeeId)),
            util.fetch.js(ApiRoutes.dailyDrivingLog.weeklyTotals(employeeId, jobDate)),
            util.fetch.js(ApiRoutes.contract.getNonFlagging()),
            util.fetch.js(ApiRoutes.employee.byId(employeeId)),
        ]);

        for (var d = 0; d < dailyEntries.length; d++) {
            dailyEntries[d].adjusting = false;
            dailyEntries[d].adjustingUnitsType = 0;
            dailyEntries[d].adjustment = new TimesheetDetailsAdjustment();
        }

        var timesheetChargeAdjustment = new TimesheetChargeAdjustment();

        this.setState((state) => ({
            date: date,
            loading: false,
            saving: false,
            chargesOpen: true,
            dailyEntries: dailyEntries,
            weeklyTotals: weeklyTotals,
            jobDate: jobDate,
            employeeId: employeeId,
            employeeName: employeeName,
            employee,
            timesheetChargeAdjustment: timesheetChargeAdjustment,
            nonFlaggingContract: nonFlaggingContract,
            isAdjustingCharge: false
        }));

    };

    onClose = () => {
        this.props.toggleShow(false);
        this.context.setFormOpened(false);
        this.props.onClose();
    };

    hourlyChargesTotal = (hourlyJob) => {

        var totalHours = 0.0;

        const hourlyTickets = (hourlyJob.scaleTickets ?? []).filter((x) => x.isTonnage == false);

        for (var t = 0; t < hourlyTickets.length; t++) {
            var hourlyCharges = hourlyTickets[t].charges;

            for (var hc = 0; hc < hourlyCharges.length; hc++) {
                if (hourlyCharges[hc].resourceTypeId == 1 && hourlyCharges[hc].removed != true)
                    totalHours += hourlyCharges[hc].value;
            }
        }
        
        return totalHours;
    }

    tonnageChargesTotal = (job) => {

        var totalHours = 0.0;

        const tonnageTickets = (job.scaleTickets ?? []).filter((x) => x.isTonnage == true);

        var start = null;
        for (var s = 0; s < tonnageTickets.length; s++) {
            var scaleTicket = tonnageTickets[s];

            for (var c = 0; c < scaleTicket.charges.length; c++) {
                var charge = scaleTicket.charges[c];

                if (charge.chargeTypeUnitsId == 3 || charge.linkedChargeTypeId > 0 || charge.removed == true)
                    continue;

                if (start == null && charge.start != null) {
                    start = charge.start;
                }

                if (charge.end != null) {

                    var startDt = moment(start);
                    var startTimeStr = startDt.format('h:mm a');
                    const startTime = moment.utc(startTimeStr, 'h:mm a');

                    var endDt = moment(charge.end);
                    var endTimeStr = endDt.format('h:mm a');
                    const endTime = moment.utc(endTimeStr, 'h:mm a');

                    var duration = moment.duration(endTime.diff(startTime));
                    if (duration.asHours() > 0 && duration.asHours() < 24)
                        totalHours += duration.asHours();

                    start = null;
                }
            }
        }

        return totalHours;
    }

    getTotalHours = () => {
        const { dailyEntries } = { ...this.state };
        var totalHours = 0;

        // Internal
        const charges = (dailyEntries ?? []).filter((x) => x.logEntryType == 2);
        for (var ch = 0; ch < charges.length; ch++) {

            if (charges[ch].removed == true)
                continue;

            totalHours += charges[ch].value;
        }

        const jobs = (dailyEntries ?? []).filter((x) => x.logEntryType == 1);

        // Hourly
        for (var hj = 0; hj < jobs.length; hj++) {
            var hourlyJob = jobs[hj];
            const hourlyTickets = (hourlyJob.scaleTickets ?? []).filter((x) => x.isTonnage == false);

            for (var t = 0; t < hourlyTickets.length; t++) {
                var hourlyCharges = hourlyTickets[t].charges;

                for (var hc = 0; hc < hourlyCharges.length; hc++) {
                    if (hourlyCharges[hc].resourceTypeId == 1 && hourlyCharges[hc].removed != true)
                        totalHours += hourlyCharges[hc].value;
                }
            }
        }

        // Tonnage
        for (var j = 0; j < jobs.length; j++) {
            var job = jobs[j];
            const scaleTickets = (job.scaleTickets ?? []).filter((x) => x.isTonnage == true);

            var start = null;
            for (var s = 0; s < scaleTickets.length; s++) {
                var scaleTicket = scaleTickets[s];
                
                for (var c = 0; c < scaleTicket.charges.length; c++) {
                    var charge = scaleTicket.charges[c];

                    if (charge.chargeTypeUnitsId == 3 || charge.linkedChargeTypeId > 0 || charge.removed == true)
                        continue;

                    if (start == null && charge.start != null) {
                        start = charge.start;
                    }

                    if (charge.end != null) {

                        var startDt = moment(start);
                        var startTimeStr = startDt.format('h:mm a');
                        const startTime = moment.utc(startTimeStr, 'h:mm a');

                        var endDt = moment(charge.end);
                        var endTimeStr = endDt.format('h:mm a');
                        const endTime = moment.utc(endTimeStr, 'h:mm a');

                        var duration = moment.duration(endTime.diff(startTime));
                        if (duration.asHours() > 0 && duration.asHours() < 24) 
                            totalHours += duration.asHours();

                        start = null;
                    }
                }
            }
        }

        return totalHours.toFixed(2);
    }

    
    getTotalTonnage = () => {
        const { dailyEntries } = { ...this.state };

        const jobs = (dailyEntries ?? []).filter((x) => x.logEntryType == 1);

        var totalTonnage = 0;
        for (var j = 0; j < jobs.length; j++) {
            var job = jobs[j];
            for (var s = 0; s < job.scaleTickets.length; s++) {
                var scaleTicket = job.scaleTickets[s];
                for (var c = 0; c < scaleTicket.charges.length; c++) {
                    var charge = scaleTicket.charges[c];
                    if (charge.chargeTypeUnitsId == 3 && charge.removed != true) {
                        totalTonnage += charge.tonnage;
                    }
                }
            }
        }

        return totalTonnage.toLocaleString('en-US');
    }

    getTotalLoads = () => {
        const { dailyEntries } = { ...this.state };

        const jobs = (dailyEntries ?? []).filter((x) => x.logEntryType == 1);

        var totalLoads = 0;
        for (var j = 0; j < jobs.length; j++) {
            var job = jobs[j];
            for (var s = 0; s < job.scaleTickets.length; s++) {
                var scaleTicket = job.scaleTickets[s];
                if (scaleTicket.isDeleted)
                    continue;

                if (scaleTicket.isTonnage) {
                    for (var c = 0; c < scaleTicket.charges.length; c++) {
                        var charge = scaleTicket.charges[c];

                        if (charge.chargeTypeUnitsId == 1 && charge.removed != true) {
                            totalLoads++;
                        }
                    }
                }

                else {
                    for (var c2 = 0; c2 < scaleTicket.charges.length; c2++) {
                        var charge2 = scaleTicket.charges[c2];

                        if (charge2.chargeTypeUnitsId == 1 && charge2.removed != true) {
                            totalLoads += charge2.loads;
                        }
                    }
                }
            }
        }

        return totalLoads;
    }

    async approveInternalCharges() {
        const { jobDate, employeeId, date, employeeName } = { ...this.state };

        this.setState({
            saving: true
        });

        await util.fetch.post(
            ApiRoutes.dailyDrivingLog.approveCharges(jobDate, employeeId),
            null,
            util.fetch.format.none,
        );
        
        toasty.success('Charges Approved');
        

        this.open(date, employeeName, employeeId);
    }

    async approveHourlyTimesheets(timesheetId) {
        const { employeeId, date, employeeName } = { ...this.state };

        this.setState({
            saving: true
        });

        await util.fetch.post(
            ApiRoutes.dailyDrivingLog.approveHourlyTimesheets(timesheetId, employeeId),
            null,
            util.fetch.format.none,
        );

        toasty.success('Hourly Charges Approved');

        this.open(date, employeeName, employeeId);
    }

    async approveTonnageTimesheets(timesheetId) {
        const { employeeId, date, employeeName } = { ...this.state };

        this.setState({
            saving: true
        });

        try {
            var ret = await util.fetch.post(
                ApiRoutes.dailyDrivingLog.approveTonnageTimesheets(timesheetId, employeeId),
                null,
                util.fetch.format.none,
            );

            if (!ret.ok)
                toasty.error('Unable to approve. End time range not provided.');
            else
                toasty.success('Tonnage Charges Approved');
        } catch (err) {
            toasty.error('Unable to approve. End time range not provided.');
        }

        this.open(date, employeeName, employeeId);
    }

    async populateState() {
        
    }

    onTimesheetEdit = async (job, timesheet) => {

        job.signedOn = timesheet.signedOn;
        job.signatureNotes = timesheet.signatureNotes;
        job.signatureData = timesheet.signatureData;

        this.timesheetEditRef.current.open(job);
    };

    editInternalCharge = async (charge) => {
        const { date, dailyEntries, timesheetChargeAdjustment, employee } = { ...this.state };

        timesheetChargeAdjustment.removeCharge = charge;
        timesheetChargeAdjustment.timesheetId = charge.timesheetId;
        timesheetChargeAdjustment.jobDate = date;
        timesheetChargeAdjustment.chargeTypeId = charge.chargeTypeId;
        timesheetChargeAdjustment.notes = 'Adjustment to charge';
        timesheetChargeAdjustment.chargeTypeName = charge.chargeTypeName;
        timesheetChargeAdjustment.resourceId = charge.chargeTypeId;
        timesheetChargeAdjustment.resourceName = charge.chargeTypeName;
        timesheetChargeAdjustment.equipmentId = charge.equipmentId;
        timesheetChargeAdjustment.equipmentName = charge.equipmentName;
        timesheetChargeAdjustment.startDate = moment.utc(charge.startDate);
        timesheetChargeAdjustment.endDate = moment.utc(charge.endDate);
        timesheetChargeAdjustment.quantity = charge.value;
        timesheetChargeAdjustment.start = moment(charge.startDate).format('h:mm a');
        timesheetChargeAdjustment.end = moment(charge.endDate).format('h:mm a');
        timesheetChargeAdjustment.quantity = charge.value;
        timesheetChargeAdjustment.chargeTypeObj = {
            resourceTypeId: charge.resourceTypeId,
            description: charge.chargeName,
            unitsId: charge.chargeTypeUnitsId,
            units: {
                description: charge.chargeTypeUnitsName,
            }
        };

        let [equipment] = await Promise.all([
            util.fetch.js(ApiRoutes.timesheet.slideoutEquipmentByEmployee(employee.dispatchCompanyId, employee.id)),
        ]);

        this.setState({
            dailyEntries,
            equipment,
            timesheetChargeAdjustment,
            isAdjustingCharge: true
        });
    };

    editCharge = async (job, scaleTicket, charge, unitsType) => {
        const { dailyEntries } = { ...this.state };

        job.adjustment.removeCharge = charge;
        job.adjustment.timesheetId = scaleTicket.timesheetId;
        job.adjustment.jobDate = scaleTicket.jobDate;
        job.adjustment.chargeTypeId = charge.chargeId;
        job.adjustment.notes = 'Adjustment to Job: ' + scaleTicket.timesheetNumber + ', Ticket: ' + scaleTicket.workOrderNumber;
        job.adjustment.chargeTypeName = charge.chargeName;
        job.adjustment.resourceId = charge.resourceId;
        job.adjustment.resourceName = charge.resourceName;
        job.adjustment.materialId = charge.materialId;
        job.adjustment.materialName = charge.materialName;
        job.adjustment.loads = charge.loads;
        job.adjustment.startDate = moment.utc(charge.start);
        job.adjustment.endDate = charge.end == null ? null : moment.utc(charge.end);
        job.adjustment.quantity = charge.value;
        job.adjustment.start = charge.chargeTypeUnitsId == 1 ? moment(charge.start).format('h:mm a') : '';
        job.adjustment.end = charge.end == null ? null : charge.chargeTypeUnitsId == 1 ? moment(charge.end).format('h:mm a') : '';
        job.adjustment.quantity = charge.value;
        job.adjustment.chargeTypeObj = {
            resourceTypeId: charge.resourceTypeId,
            description: charge.chargeName,
            unitsId: charge.chargeTypeUnitsId,
            units: {
                description: charge.chargeTypeUnitsName,
            }
        };

        let [chargeTypes, equipment] = await Promise.all([
            util.fetch.js(ApiRoutes.chargeType.byContract(job.contractId)),
            util.fetch.js(ApiRoutes.timesheet.slideoutEquipment(job.dispatchCompanyId)),
        ]);

        const haulNumbers = [];
        for (var i = 0; i < job.scaleTickets.length; i++) {
            haulNumbers.push({
                id: job.scaleTickets[i].timesheetId,
                description: job.scaleTickets[i].timesheetNumber,
            });
        }

        job.adjustingUnitsType = unitsType;
        job.adjusting = true;

        this.setState({
            dailyEntries,
            chargeTypes,
            equipment,
            haulNumbers
        });
    };

    onAdjustInternalCharge = async () => {

        const { employee } = { ...this.state };

        let [equipment] = await Promise.all([
            util.fetch.js(ApiRoutes.timesheet.slideoutEquipmentByEmployee(employee.dispatchCompanyId, employee.id)),
        ]);

        this.setState({
            equipment,
            isAdjustingCharge: true,
        });
    }

    cancelInternalChargeAdjustment = async () => {

        var timesheetChargeAdjustment = new TimesheetChargeAdjustment();

        this.setState(() => ({
            timesheetChargeAdjustment: timesheetChargeAdjustment,
            isAdjustingCharge: false
        }));
    };

    onAdjustClick = async (job, unitsType) => {
        const { dailyEntries } = { ...this.state };

        let [chargeTypes, equipment] = await Promise.all([
            util.fetch.js(ApiRoutes.chargeType.byContract(job.contractId)),
            util.fetch.js(ApiRoutes.timesheet.slideoutEquipment(job.dispatchCompanyId)),
        ]);

        const haulNumbers = [];
        for (var i = 0; i < job.scaleTickets.length; i++) {
            haulNumbers.push({
                id: job.scaleTickets[i].timesheetId,
                description: job.scaleTickets[i].timesheetNumber,
            });
        }

        job.adjustingUnitsType = unitsType;
        job.adjusting = true;

        this.setState({
            dailyEntries,
            chargeTypes,
            equipment,
            haulNumbers
        });
    }

    saveInternalChargeAdjustment = async () => {

        const { date, nonFlaggingContract, timesheetChargeAdjustment } = { ...this.state };

        if (parseFloat(timesheetChargeAdjustment.quantity) == 0) {
            toasty.error('Start time cannot be greater than end time');
            return;
        }

        if (timesheetChargeAdjustment.chargeTypeObj.resourceId == 0
            || timesheetChargeAdjustment.notes == null || timesheetChargeAdjustment.notes.length == 0
            || timesheetChargeAdjustment.start == null || timesheetChargeAdjustment.end == null)
            return;

        // Validate that stop time is greater than start time.
        // start/end can be in 'HH:mm' (after edit) or 'h:mm a' (initial load) — try both.
        //const mStart = moment(timesheetChargeAdjustment.start, ['HH:mm', 'h:mm a']);
        //const mEnd = moment(timesheetChargeAdjustment.end, ['HH:mm', 'h:mm a']);
        //if (mEnd.isSameOrBefore(mStart)) {
        //    toasty.error('Stop time must be greater than start time');
        //    return;
        //}

        if (timesheetChargeAdjustment.equipmentId == 0) {
            return;
        }

        const { employeeId } = { ...this.state };

        var start = moment(timesheetChargeAdjustment.startDate).utc().format('HH:mm');
        var end = moment(timesheetChargeAdjustment.endDate).utc().format('HH:mm');

        this.setState({
            saving: true
        });

        await util.fetch.andGetResponse(
            util.fetch.types.post,
            ApiRoutes.selfDispatchTimesheet.create(),
            {
                isAdjustment: true,
                include: true,
                contractId: nonFlaggingContract.id,
                contractDescription: nonFlaggingContract.description,
                date: date,
                isComplete: false,
                isDeleted: false,
                nonFlagging: true,
                employeeId: employeeId,
                timeEntries: [{
                    chargeTypeId: timesheetChargeAdjustment.resourceId,
                    chargeTypeName: timesheetChargeAdjustment.resourceName,
                    equipmentId: timesheetChargeAdjustment.equipmentId,
                    equipmentName: timesheetChargeAdjustment.equipmentName,
                    employeeId: employeeId,
                    chargeTypeUnitsId: 1,
                    chargeTypeUnitsName: 'Hours',
                    notes: timesheetChargeAdjustment.notes,
                    start: start,
                    end: end
                }]
            },
            'Error Creating Timesheet',
        );

        
        this.refresh();
    };

    saveAdjustment = async (job, isTonnage) => {

        if (!isTonnage && parseFloat(job.adjustment.quantity) == 0) {
            toasty.error('Start time cannot be greater than end time');
            return;
        }

        if (job.adjustment.timesheetId == 0
            || job.adjustment.chargeTypeObj == null
            || job.adjustment.notes == null || job.adjustment.notes.length == 0)
            return;

        // Validate that stop time is greater than start time.
        // For tonnage, end is optional — skip the check when end is omitted.
        // start/end can be in 'HH:mm' (after edit) or 'h:mm a' (initial load) — try both.
        //if (job.adjustment.start && job.adjustment.end) {
        //    const mStart = moment(job.adjustment.start, ['HH:mm', 'h:mm a']);
        //    const mEnd = moment(job.adjustment.end, ['HH:mm', 'h:mm a']);
        //    if (mEnd.isSameOrBefore(mStart)) {
        //        toasty.error('Stop time must be greater than start time');
        //        return;
        //    }
        //}

        const { employeeId } = { ...this.state };

        let tsJobDate = null;
        if (job.scaleTickets.length > 0) {
            tsJobDate = job.scaleTickets[0].jobDate;
        }

        // Make sure the times use the job date 
        var jobDate = moment(tsJobDate);

        var startDate = moment(job.adjustment.startDate);
        startDate.set({
            'year': jobDate.year(),
            'month': jobDate.month(),
            'date': jobDate.date(),
        })

        this.setState({
            saving: true
        });

        if (isTonnage && job.adjustment.endDate == null) {

            //Save
            await util.fetch.post(
                ApiRoutes.dailyDrivingLog.adjustment(),
                {
                    timesheetId: job.adjustment.timesheetId,
                    jobDate: tsJobDate,
                    start: job.adjustment.startDate ? moment(startDate).toDate().toISOString() : null,
                    end: null,
                    resourceName: job.adjustment.resourceName,
                    resourceId: job.adjustment.resourceId ? job.adjustment.resourceId : 0,
                    resourceTypeId: job.adjustment.chargeTypeObj.resourceTypeId,
                    chargeTypeName: job.adjustment.chargeTypeName,
                    chargeTypeId: job.adjustment.chargeTypeId,
                    quantity: parseFloat(job.adjustment.quantity),
                    notes: job.adjustment.notes,
                    unitsId: job.adjustment.chargeTypeObj.unitsId,
                    unitsName: job.adjustment.chargeTypeObj.units.description,
                    employeeId: employeeId,
                    loads: 1,
                    materialId: job.adjustment.materialId
                },
            );

        }

        else {
            var endDate = moment(job.adjustment.endDate);
            endDate.set({
                'year': jobDate.year(),
                'month': jobDate.month(),
                'date': jobDate.date(),
            })

            //Save
            await util.fetch.post(
                ApiRoutes.dailyDrivingLog.adjustment(),
                {
                    timesheetId: job.adjustment.timesheetId,
                    jobDate: tsJobDate,
                    start: job.adjustment.startDate ? moment(startDate).toDate().toISOString() : null,
                    end: job.adjustment.endDate ? moment(endDate).toDate().toISOString() : null,
                    resourceName: job.adjustment.resourceName,
                    resourceId: job.adjustment.resourceId ? job.adjustment.resourceId : 0,
                    resourceTypeId: job.adjustment.chargeTypeObj.resourceTypeId,
                    chargeTypeName: job.adjustment.chargeTypeName,
                    chargeTypeId: job.adjustment.chargeTypeId,
                    quantity: parseFloat(job.adjustment.quantity),
                    notes: job.adjustment.notes,
                    unitsId: job.adjustment.chargeTypeObj.unitsId,
                    unitsName: job.adjustment.chargeTypeObj.units.description,
                    employeeId: employeeId,
                    loads: parseInt(job.adjustment.loads),
                    materialId: job.adjustment.materialId
                },
            );
        }

        if (job.adjustment?.removeCharge)
            await this.removeCharge(job.adjustment.removeCharge);

        this.refresh();
    };

    refresh = async () => {
        const { employeeId, jobDate } = { ...this.state };

        let [dailyEntries, weeklyTotals] = await Promise.all([
            util.fetch.js(ApiRoutes.dailyDrivingLog.byEmployee(jobDate, employeeId)),
            util.fetch.js(ApiRoutes.dailyDrivingLog.weeklyTotals(employeeId, jobDate))
        ]);

        for (var d = 0; d < dailyEntries.length; d++) {
            dailyEntries[d].adjustingUnitsType = 0;
            dailyEntries[d].adjusting = false;
            dailyEntries[d].adjustment = new TimesheetDetailsAdjustment();
        }

        var timesheetChargeAdjustment = new TimesheetChargeAdjustment();

        this.setState(() => ({
            dailyEntries: dailyEntries,
            weeklyTotals: weeklyTotals,
            timesheetChargeAdjustment: timesheetChargeAdjustment,
            isAdjustingCharge: false,
            saving: false
        }));
    }

    cancelAdjustment = async (dailyEntries, job) => {
        job.adjusting = false;
        job.adjustingUnitsType = 0;
        job.adjustment = new TimesheetDetailsAdjustment();

        this.setState(() => ({
            dailyEntries
        }));
    };

    onInternalChargeTimeEntryChanged = (ev) => {
        const { date, timesheetChargeAdjustment } = { ...this.state };

        const val = ev.target.value;
        const { name } = ev.target;

        // We display the options in 12-hour format, but the db has 24.
        const timeValue = ev.target.value
            ? moment(val, ['h:mm A']).format('HH:mm')
            : '';

        timesheetChargeAdjustment[name] = timeValue;
        this.setAdjustmentTimeQuantity(date, timesheetChargeAdjustment);

        this.setState(() => ({
            timesheetChargeAdjustment
        }));
    };

    onAdjustmentTimeEntryChanged = (dailyEntries, j, ev) => {
        const val = ev.target.value;
        const { name } = ev.target;

        // We display the options in 12-hour format, but the db has 24.
        const timeValue = ev.target.value
            ? moment(val, ['h:mm A']).format('HH:mm')
            : '';

        if (name == 'end' && val == '') {
            j.adjustment.end = null;
            j.adjustment.endDate = null;
            j.adjustment.quantity = 0;

            this.setAdjustmentTimeQuantity(j.startDate, j.adjustment);
        }

        else {
            j.adjustment[name] = timeValue;
            this.setAdjustmentTimeQuantity(j.startDate, j.adjustment);
        }

        this.setState(() => ({
            dailyEntries
        }));
    };

    setAdjustmentTimeQuantity = (jobStart, adj) => {

        // Calculate the duration between Start and End.  Factor in if this enters into the next day.
        // Use the job start date/time as opposed to moment defaulting to the current day when getting duration.
        if (adj.start) {
            const time_format1 = 'HH:mm';
            const time_format2 = 'h:mm a';
            const date_format = 'YYYY-MM-DD';

            const mStartDate = moment.utc(jobStart);
            const mStartTime = adj.start.indexOf('m') == 0 ? moment.utc(adj.start, time_format1) : moment.utc(adj.start, time_format2);

            adj.startDate = moment.utc(
                `${mStartDate.format(date_format)} ${mStartTime.format(
                    time_format1,
                )}`,
            ).valueOf();

            if (adj.end == null) {
                adj.endDate = null;
                adj.quantity = 0;
            }
            else {
                const mEndDate = moment.utc(jobStart);
                const mEndTime = adj.end.indexOf('m') == 0 ? moment.utc(adj.end, time_format1) : moment.utc(adj.end, time_format2);

                if (mEndTime.isBefore(mStartTime)) {
                    mEndTime.add(1, 'day');
                    mEndDate.add(1, 'day');
                }

                adj.endDate = moment.utc(
                    `${mEndDate.format(date_format)} ${mEndTime.format(
                        time_format1,
                    )}`,
                ).valueOf();

                const dur = moment.duration(mEndTime.diff(mStartTime));
                adj.quantity = dur.asHours().toFixed(2);
                
            }
        }
    };

    removeCharge = async (id) => {
        await util.fetch.post(ApiRoutes.dailyDrivingLog.removeCharge(id));
        this.refresh();
    };

    render() {
        const {
            loading,
            saving,
            chargesOpen,
            dailyEntries,
            weeklyTotals,
            jobDate,
            employeeId,
            employeeName,
            chargeTypes,
            equipment,
            haulNumbers, 
            isAdjustingCharge,
            timesheetChargeAdjustment,
            nonFlaggingContract
        } = this.state;

        const {
            showNav,
        } = { ...this.props };

        const tenantSettings = ((this.context ?? {}).tenant ?? {}).tenantSettings ?? {};

        var formattedDate = moment(jobDate, 'YYYYMMDD').format('dddd, MMMM Do YYYY');

        const jobs = (dailyEntries ?? []).filter((x) => x.logEntryType == 1);
        const charges = (dailyEntries ?? []).filter((x) => x.logEntryType == 2);

        const allChargesApproved = (charges ?? []).filter((x) => x.status == 'Approved').length == (charges ?? []).length;
        const allChargesSubmitted = charges.length > 0 && charges.filter((x) => x.status == 'Unsubmitted').length == 0;

        const totalTonnage = this.getTotalTonnage();

        var totalCharges = 0.00;
        for (var i = 0; i < charges.length; i++) {
            if (charges[i].removed == true)
                continue;
            totalCharges += charges[i].value;
        }

        return (
            <>
                <SlideForm
                    size="col-xl-12 col-md-12 col-xs-12"
                    loading={loading || !tenantSettings}
                    show={this.props.show}
                    id="timesheetForm"
                    formIcon={faUserClock}
                    formTitle="Daily Driving Log"
                    ref={this.formRef}
                    setIsValidated={(value) => {
                        this.setState({ formValidated: value });
                    }}
                    isValidated={true}
                    isValid={(value) => {
                        if (!value) {
                            toasty.error(
                                'Validation Failed. Please review and complete all required data',
                            );
                        }
                    }}
                    className="pb-2 w-100"
                    hideToolbar
                    onSubmit={this.onSubmit}
                    onClose={this.onClose}
                    errors={this.state.errors}
                    onClearErrors={this.onClearErrors}
                    readOnly={false}
                    showNav={showNav}
                    onNavBack={this.props.onNavBack}
                    onNavForward={this.props.onNavForward}
                    saveButtonText="Save Ticket"
                >
                    {loading && (
                        <div
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                zIndex: 9999,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                pointerEvents: 'all',
                            }}
                        >
                            <div style={{ textAlign: 'center', color: '#fff' }}>
                                <i className="fa fa-spinner fa-spin fa-3x" />
                                <div style={{ marginTop: '10px', fontSize: '16px' }}>Loading...</div>
                            </div>
                        </div>
                    )}

                    <div className="job-confirm-details" style={{ marginLeft: '15px', marginRight: '15px', marginTop: '5px' }}>

                        <table style={{ width: '450px', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                            <tbody style={{ verticalAlign: 'middle' }}>
                                <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                                    <td style={{ width: '60px', padding: '8px 4px 4px' }}>
                                        <FormLabel htmlFor="jobStartTime" text="Date" />
                                    </td>
                                    <td colSpan={3} style={{ padding: '8px 4px 4px' }}>
                                        <span className="form-control-sm form-control-plaintext">
                                            {formattedDate ?? ''}
                                        </span>
                                    </td>
                                </tr>
                                <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                                    <td style={{ width: '60px', padding: '8px 4px 4px' }}>
                                        <FormLabel htmlFor="jobStartTime" text="Driver" />
                                    </td>
                                    <td colSpan={3} style={{ padding: '8px 4px 4px' }}>
                                        <span className="form-control-sm form-control-plaintext">
                                            {employeeName}
                                        </span>
                                    </td>
                                </tr>
                                <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                                    <td style={{ width: '60px', padding: '8px 4px 4px' }}>
                                        <FormLabel htmlFor="jobStartTime" text="Hours" />
                                    </td>
                                    <td style={{ width: '120px', padding: '8px 4px 4px' }}>
                                        <span className="form-control-sm form-control-plaintext">
                                            {this.getTotalHours(dailyEntries)}
                                        </span>
                                    </td>
                                    <td style={{ width: '120px', padding: '8px 4px 4px', paddingLeft: '30px' }}>
                                        <FormLabel htmlFor="jobStartTime" text="Week Hours" />
                                    </td>
                                    <td style={{ padding: '8px 4px 4px' }}>
                                        <span className="form-control-sm form-control-plaintext">
                                            {weeklyTotals?.hours?.toFixed(2)}
                                        </span>
                                    </td>
                                </tr>
                                <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                                    <td style={{ padding: '8px 4px 4px' }}>
                                        <FormLabel htmlFor="jobStartTime" text="Tonnage" />
                                    </td>
                                    <td style={{ padding: '8px 4px 4px' }}>
                                        <span className="form-control-sm form-control-plaintext">
                                            {totalTonnage}
                                        </span>
                                    </td>
                                    <td style={{ padding: '8px 4px 4px', paddingLeft: '30px' }}>
                                        <FormLabel htmlFor="jobStartTime" text="Week Tonnage" />
                                    </td>
                                    <td style={{ padding: '8px 4px 4px' }}>
                                        <span className="form-control-sm form-control-plaintext">
                                            {weeklyTotals?.tonnage}
                                        </span>
                                    </td>
                                </tr>
                                <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                                    <td style={{ padding: '8px 4px 4px' }}>
                                        <FormLabel htmlFor="jobStartTime" text="Loads" />
                                    </td>
                                    <td style={{ padding: '8px 4px 4px' }}>
                                        <span className="form-control-sm form-control-plaintext">
                                            {this.getTotalLoads()}
                                        </span>
                                    </td>
                                    <td style={{ padding: '8px 4px 4px', paddingLeft: '30px' }}>
                                        <FormLabel htmlFor="jobStartTime" text="Week Loads" />
                                    </td>
                                    <td style={{ padding: '8px 4px 4px' }}>
                                        <span className="form-control-sm form-control-plaintext">
                                            {weeklyTotals?.loads}
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
          
                    <CollapseUnderlineHeader
                        className="timesheet-details-collapse"
                        headerText='Charges'
                        isOpen={chargesOpen}
                        toggleCollapse={() => this.setState({
                            chargesOpen: !chargesOpen,
                        })}
                    >

                        
                        <div className="details table-responsive">
                            <table className="table table-sm table-bordered table-striped timesheet-details-table">
                                <thead className="text-muted">
                                    <tr>
                                        <th className="isadjusted" />
                                        <th className="detailscharge">Charge</th>
                                        <th className="detailsnote">Equipment</th>
                                        <th className="detailsnote">Notes</th>
                                        <th className="detailsstart">Start</th>
                                        <th className="detailsend">End</th>
                                        <th className="detailsend">Quantity</th>
                                        <th className="detailsunits">Units</th>
                                        <th className="detailsunits">Status</th>
                                        <th className="detailsunits"></th>
                                        <th className="detailsunits"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {charges.map((d, idx) => (
                                        <tr key={d.id}
                                            className={`timesheet-details-table-row${d.removed ? ' strikeout' : ''}`}>

                                            <td className="isadjusted">
                                                {!!d.isAdjustment && (
                                                    <Badge className="p-1">
                                                        ADJ
                                                    </Badge>
                                                )}
                                            </td>
                                            <td>{d.chargeTypeName}</td>
                                            <td>{d.equipmentName}</td>
                                            <td>
                                                {!!d.notes && (
                                                    <div className="userNote">
                                                        <div className="noteContent">
                                                            {
                                                                d.notes
                                                            }
                                                        </div>
                                                        <div className="noteContentByLine">
                                                            {`${d.createdBy}, ${moment(d.createdOn).fromNow()}`}
                                                        </div>
                                                    </div>
                                                )}
                                            </td>
                                            <td align="right">{moment(d.startDate).format('h:mm a')}</td>
                                            <td align="right">{moment(d.endDate).format('h:mm a')}</td>
                                            <td align="right">{d.value}</td>
                                            <td align="center">Hours</td>

                                            {d.status == 'Submitted' &&
                                                <td align="center" style={{ color: 'blue' }}>{d.status}</td>
                                            }

                                            {d.status == 'Unsubmitted' &&
                                                <td align="center" style={{ color: 'red' }}>{d.status}</td>
                                            }

                                            {d.status != 'Unsubmitted' && d.status != 'Submitted' &&
                                                <td align="center">{d.status}</td>
                                            }

                                            <td className="detailsdelete">
                                                {d.status == 'Submitted' && (

                                                    <FontAwesomeIcon
                                                        className={`details-edit-button mt-1 cursor-pointer${d.removed ? ' edit' : ''}`}
                                                        title="edit this charge"
                                                        icon={faPencil}
                                                        onClick={() => this.editInternalCharge(d)}
                                                    />
                                                )}
                                            </td>
                                            <td className="detailsdelete">
                                                {d.status == 'Submitted' && (
                                                    <FontAwesomeIcon
                                                        className={`details-remove-button text-danger mt-1 cursor-pointer${d.removed ? ' removed' : ''}`}
                                                        title="Strike this charge"
                                                        icon={faTrash}
                                                        onClick={() => this.removeCharge(d.id)}
                                                    />
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colspan="4">&nbsp;</td>
                                        <td align="right"><b>Total</b></td>
                                        <td align="right"><b>{totalCharges.toFixed(2)}</b></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>

                        {isAdjustingCharge && (
                            <div className="details">
                                <small className="text-warning font-weight-bold">
                                    ADD ADJUSTMENTS
                                </small>
                                <table className="table table-sm table-bordered table-striped adjustment-table">
                                    <thead className="text-muted">
                                        <tr>
                                            <th className="detailscharge">
                                                Charge
                                            </th>
                                            <th className="detailsnote">
                                                Equipment
                                            </th>
                                            <th className="detailsnote">
                                                Notes
                                            </th>
                                            <th className="detailsstart">
                                                Start
                                            </th>
                                            <th className="detailsend">
                                                End
                                            </th>
                                            <th className="detailsqty">
                                                Quantity
                                            </th>
                                            <th className="detailsunits">
                                                Units
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>
                                                <FormGroup className="mb-0">

                                                    <ValidatedSelect
                                                        styles={CompactSelectStyles}
                                                        minMenuHeight={100}
                                                        maxMenuHeight={200}
                                                        name="adjustmentResourceDropdown"
                                                        required
                                                        hideClear
                                                        options={nonFlaggingContract.contractChargeTypes}
                                                        getOptionLabel={(option) => option.description}
                                                        getOptionValue={(option) => option.chargeTypeId}
                                                        value={(nonFlaggingContract.contractChargeTypes ?? []).find((x) => x.chargeTypeId === timesheetChargeAdjustment.resourceId) ?? ''}
                                                        onChange={(selection, event) => {
                                                            timesheetChargeAdjustment.resourceId = selection.chargeTypeId;
                                                            timesheetChargeAdjustment.resourceName = selection.description;
                                                            timesheetChargeAdjustment.accountCodingTypeId = selection.accountCodingTypeId;
                                                            this.setState({ dailyEntries });
                                                        }}
                                                        validationMessage="Charge is required."
                                                    />
                                                    
                                                </FormGroup>
                                            </td>
                                            <td>
                                                <FormGroup className="mb-0">

                                                    {(timesheetChargeAdjustment.accountCodingTypeId == 2 || timesheetChargeAdjustment.accountCodingTypeId == 5 || timesheetChargeAdjustment.accountCodingTypeId == 4) &&
                                                        <ValidatedSelect
                                                            styles={CompactSelectStyles}
                                                            minMenuHeight={100}
                                                            maxMenuHeight={200}
                                                            name="adjustmentEquipmentDropdown"
                                                            hideClear
                                                            required
                                                            options={equipment}
                                                            getOptionLabel={(option) => option.description}
                                                            getOptionValue={(option) => option.id}
                                                            value={(equipment ?? []).find((x) => x.id === timesheetChargeAdjustment.equipmentId) ?? ''}
                                                            onChange={(selection, event) => {
                                                                timesheetChargeAdjustment.equipmentId = selection.id;
                                                                timesheetChargeAdjustment.equipmentName = selection.description;

                                                                this.setState({ dailyEntries });
                                                            }}
                                                            validationMessage="Equipment is required."
                                                        />
                                                    }

                                                    {(timesheetChargeAdjustment.accountCodingTypeId != 2 && timesheetChargeAdjustment.accountCodingTypeId != 5 && timesheetChargeAdjustment.accountCodingTypeId != 4) &&
                                                        <ValidatedSelect
                                                            styles={CompactSelectStyles}
                                                            minMenuHeight={100}
                                                            maxMenuHeight={200}
                                                            name="adjustmentEquipmentDropdown"
                                                            hideClear
                                                            options={equipment}
                                                            getOptionLabel={(option) => option.description}
                                                            getOptionValue={(option) => option.id}
                                                            value={(equipment ?? []).find((x) => x.id === timesheetChargeAdjustment.equipmentId) ?? ''}
                                                            onChange={(selection, event) => {
                                                                timesheetChargeAdjustment.equipmentId = selection.id;
                                                                timesheetChargeAdjustment.equipmentName = selection.description;

                                                                this.setState({ dailyEntries });
                                                            }}
                                                        />
                                                    }


                                                </FormGroup>
                                            </td>
                                            <td>
                                                <FormGroup className="mb-0">
                                                    <Input
                                                        required
                                                        type="textarea"
                                                        name="notes"
                                                        value={timesheetChargeAdjustment.notes}
                                                        onChange={(evt) => {
                                                            const { value } = evt.target;
                                                            timesheetChargeAdjustment.notes = value;
                                                            this.setState({ dailyEntries });
                                                        }}
                                                        validationMessage="Notes are required."
                                                    />
                                                </FormGroup>
                                            </td>
                                            <td>
                                                <TimeEntry
                                                    name="start"
                                                    value={
                                                        timesheetChargeAdjustment.start
                                                    }
                                                    increment={0.083}
                                                    onChange={(ev) => this.onInternalChargeTimeEntryChanged(ev)}
                                                    validationMessage="Start is required."
                                                />
                                            </td>
                                            <td>
                                                <TimeEntry
                                                    name="end"
                                                    value={
                                                        timesheetChargeAdjustment.end
                                                    }
                                                    increment={0.083}
                                                    onChange={(ev) => this.onInternalChargeTimeEntryChanged(ev)}
                                                    validationMessage="End is required."
                                                />
                                            </td>
                                            <td>
                                                <NumericInput
                                                    className="form-control-sm text-right"
                                                    name="quantity"
                                                    required
                                                    value={timesheetChargeAdjustment.quantity}
                                                    type="number"
                                                    preventNegative
                                                    onChange={(event) => {
                                                        const val = event.target.value;
                                                        timesheetChargeAdjustment.quantity = val;

                                                        this.setState({ dailyEntries });
                                                    }}
                                                />
                                            </td>
                                            <td align="center">
                                                Hours
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        )}

                        <FlexCenterRow>

                            {!isAdjustingCharge && 
                                <>
                                    <Button
                                        className="mb-2"
                                        size="sm"
                                        type="button"
                                        color="secondary"
                                        onClick={() => {
                                            this.onAdjustInternalCharge();
                                        }}
                                    >
                                        <i className="fa fa-pen-square fa-lg mr-2" />
                                        Add Adjustment
                                    </Button>

                                    &nbsp;&nbsp;

                                    {!allChargesApproved && allChargesSubmitted &&
                                        <Button
                                            disabled={!!saving}
                                            className="mb-2"
                                            size="sm"
                                            color="success"
                                            onClick={() => {
                                                this.approveInternalCharges();
                                            }}
                                        >
                                            Approve
                                        </Button>
                                    }
                                </>
                            }

                            {isAdjustingCharge &&
                                <>
                                    <Button
                                        disabled={!!saving}
                                        className="mb-2"
                                        size="sm"
                                        color="success"
                                        onClick={() => {
                                            this.saveInternalChargeAdjustment();
                                        }}
                                    >
                                        Save
                                    </Button>

                                    &nbsp;&nbsp;

                                    <Button
                                        disabled={!!saving}
                                        className="mb-2"
                                        size="sm"
                                        color="secondary"
                                        onClick={() => {
                                            this.cancelInternalChargeAdjustment();
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </>
                            }
                        </FlexCenterRow>

                    </CollapseUnderlineHeader>
                    
                    {jobs.map((j, idx) => {

                        var allTSSubmitted = false;
                        var allTSApproved = false;
                        var isHourlyAdjusting = j.adjusting && j.adjustingUnitsType == 1;

                        const hourlyJobs = (j.scaleTickets ?? []).filter((x) => x.isTonnage == false);
                        var hourlyJobTotal = this.hourlyChargesTotal(j);

                        if (hourlyJobs.length == 0) return (
                            <span></span>
                        );

                        var materials = [];
                        for (var m = 0; m < j.jobMaterials.length; m++) {
                            materials.push({ label: j.jobMaterials[m].description, value: j.jobMaterials[m].id })
                        }

                        return (
                            <CollapseUnderlineHeader
                                className="timesheet-details-collapse"
                                headerText={j.customer + ' - Hourly'}
                                isOpen={true}
                            >

                                <div className="details table-responsive">
                                    <table className="table table-sm table-bordered table-striped timesheet-details-table">
                                        <thead className="text-muted">
                                            <tr>
                                                <th className="isadjusted" />
                                                <th className="detailscharge">Job / Haul #</th>
                                                <th className="detailscharge">Charge</th>
                                                <th className="detailsnote">Resource</th>
                                                <th className="detailsnote">Loads - Material</th>
                                                <th className="detailsnote">Notes</th>
                                                <th className="detailsstart">Start</th>
                                                <th className="detailsend">End</th>
                                                <th className="detailsend">Quantity</th>
                                                <th className="detailsunits">Units</th>
                                                <th className="detailsunits">Status</th>
                                                <th className="detailsunits"></th>
                                                <th className="detailsunits"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {hourlyJobs.map((s, idx) => {

                                                allTSSubmitted = s.charges.length > 0
                                                    && s.charges.filter((x) => x.status == 'Unsubmitted').length == 0;

                                                allTSApproved = (s.charges ?? []).filter((x) => x.status == 'Approved').length == (s.charges ?? []).length;

                                                var isTonnage = s.charges.filter((x) => x.chargeTypeUnitsId == 3).length > 0;

                                                return (
                                                    s.charges.map((c, idx) => (
                                                        <tr key={c.id}
                                                            className={`timesheet-details-table-row${c.removed ? ' strikeout' : ''}`}>

                                                            <td className="isadjusted">
                                                                {!!c.isAdjustment && (
                                                                    <Badge className="p-1">
                                                                        ADJ
                                                                    </Badge>
                                                                )}
                                                            </td>

                                                            <td><span className="site-link" onClick={() => this.onTimesheetEdit(j, s)}>{s.timesheetNumber}</span></td>
                                                            <td>{c.chargeName}</td>
                                                            <td>{c.resourceName}</td>
                                                            <td>{c.resourceTypeId == 1 || c.loads == 0 ? '' : c.materialId > 0 ? c.loads == 1 ? c.loads + ' load - ' + c.materialName : c.loads + ' loads - ' + c.materialName : '' }</td>
                                                            <td>
                                                                {!!c.notes && (
                                                                    <div className="userNote">
                                                                        <div className="noteContent">
                                                                            {
                                                                                c.notes
                                                                            }
                                                                        </div>
                                                                        <div className="noteContentByLine">
                                                                            {`${c.createdBy}, ${moment(c.createdOn).fromNow()}`}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td align="right">{c.chargeTypeUnitsId == 1 && c.start != null ? moment(c.start).format('h:mm a') : ''}</td>
                                                            <td align="right">{c.chargeTypeUnitsId == 1 && c.end != null ? moment(c.end).format('h:mm a') : ''}</td>
                                                            <td align="right">{isTonnage && c.chargeTypeUnitsId == 1 ? '' : c.value}</td>
                                                            <td align="center">{c.chargeTypeUnitsId == 1 ? 'Hours' : 'Tonnage'}</td>

                                                            {c.status == 'Submitted' &&
                                                                <td align="center" style={{ color: 'blue' }}>{c.status}</td>
                                                            }

                                                            {c.status == 'Unsubmitted' &&
                                                                <td align="center" style={{ color: 'red' }}>{c.status}</td>
                                                            }

                                                            {c.status != 'Unsubmitted' && c.status != 'Submitted' &&
                                                                <td align="center">{c.status}</td>
                                                            }
                                                            <td className="detailsdelete">
                                                                {c.status == 'Submitted' && (

                                                                    <FontAwesomeIcon
                                                                        className={`details-edit-button mt-1 cursor-pointer${c.removed ? ' edit' : ''}`}
                                                                        title="edit this charge"
                                                                        icon={faPencil}
                                                                        onClick={() => this.editCharge(j, s, c, 1)}
                                                                    />
                                                                )}
                                                            </td>
                                                            <td className="detailsdelete">
                                                                {c.status == 'Submitted' && (
                                                                    <FontAwesomeIcon
                                                                        className={`details-remove-button text-danger mt-1 cursor-pointer${c.removed ? ' removed' : ''}`}
                                                                        title="Strike this charge"
                                                                        icon={faTrash}
                                                                        onClick={() => this.removeCharge(c.timesheetDetailId)}
                                                                    />
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))
                                                )
                                            })}

                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td colspan="6">&nbsp;</td>
                                                <td align="right"><b>Total</b></td>
                                                <td align="right"><b>{hourlyJobTotal.toFixed(2)}</b></td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>

                                {isHourlyAdjusting && (
                                    <div className="details">
                                        <small className="text-warning font-weight-bold">
                                            ADD ADJUSTMENTS
                                        </small>
                                        <table className="table table-sm table-bordered table-striped adjustment-table">
                                            <thead className="text-muted">
                                                <tr>
                                                    <th className="detailscharge">
                                                        Job / Haul #
                                                    </th>
                                                    <th className="detailscharge">
                                                        Charge
                                                    </th>
                                                    <th className="detailsname">
                                                        Resource
                                                    </th>
                                                    <th className="detailsnote">
                                                        Notes
                                                    </th>
                                                    <th className="detailsstart">
                                                        Start
                                                    </th>
                                                    <th className="detailsend">
                                                        End
                                                    </th>
                                                    <th className="detailsqty">
                                                        Quantity
                                                    </th>
                                                    <th className="detailsunits">
                                                        Units
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>
                                                        <FormGroup className="mb-0">
                                                            <ValidatedSelect
                                                                styles={CompactSelectStyles}
                                                                minMenuHeight={100}
                                                                maxMenuHeight={200}
                                                                name="adjustmentTimesheetNumberDropdown"
                                                                required
                                                                hideClear
                                                                options={haulNumbers}
                                                                getOptionLabel={(option) => option.description}
                                                                getOptionValue={(option) => option.id}
                                                                value={(haulNumbers ?? []).find((x) => x.id === j.adjustment.timesheetId) ?? ''}
                                                                onChange={(selection, event) => {
                                                                    j.adjustment.timesheetId = selection.id;
                                                                    this.setState({ dailyEntries });
                                                                }}
                                                                validationMessage="Haul # is required."
                                                            />
                                                        </FormGroup>
                                                    </td>
                                                    <td>
                                                        <FormGroup className="mb-0">
                                                            <ValidatedSelect
                                                                styles={CompactSelectStyles}
                                                                minMenuHeight={100}
                                                                maxMenuHeight={200}
                                                                name="adjustmentChargeTypeDropdown"
                                                                required
                                                                hideClear
                                                                options={chargeTypes}
                                                                getOptionLabel={(option) => option.description}
                                                                getOptionValue={(option) => option.id}
                                                                value={(chargeTypes ?? []).find((x) => x.id === j.adjustment.chargeTypeId) ?? ''}
                                                                onChange={(selection, event) => {
                                                                    j.adjustment.chargeTypeId = selection.id;
                                                                    j.adjustment.chargeTypeName = selection.description;
                                                                    j.adjustment.chargeTypeObj = selection;
                                                                    j.adjustment.resourceId = employeeId;
                                                                    j.adjustment.resourceName = employeeName;
                                                                    this.setState({ dailyEntries });
                                                                }}
                                                                validationMessage="Charge type is required."
                                                            />
                                                        </FormGroup>
                                                    </td>
                                                    <td>
                                                        <FormGroup className="mb-0">

                                                            {j.adjustment.chargeTypeObj?.resourceTypeId == 1 &&
                                                                <div style={{ marginTop: '5px' }}>{employeeName}</div>
                                                            }

                                                            {j.adjustment.chargeTypeObj?.resourceTypeId == 2 &&
                                                                <ValidatedSelect
                                                                    styles={CompactSelectStyles}
                                                                    minMenuHeight={100}
                                                                    maxMenuHeight={200}
                                                                    name="adjustmentResourceDropdown"
                                                                    required
                                                                    hideClear
                                                                    options={equipment}
                                                                    getOptionLabel={(option) => option.description}
                                                                    getOptionValue={(option) => option.id}
                                                                    value={(equipment ?? []).find((x) => x.id === j.adjustment.resourceId) ?? ''}
                                                                    onChange={(selection, event) => {
                                                                        j.adjustment.resourceId = selection.id;
                                                                        j.adjustment.resourceName = selection.description;

                                                                        this.setState({ dailyEntries });
                                                                    }}
                                                                    validationMessage="Equipment is required."
                                                                />
                                                            }

                                                        </FormGroup>

                                                        {j.adjustment.chargeTypeObj?.resourceTypeId == 2 &&
                                                            <table width="100%">
                                                                <tr style={{ backgroundColor: 'transparent' }}>
                                                                    <td style={{ padding: 0, border: 0, paddingTop: '9px' }} align="right">
                                                                        <span style={{ fontSize: '12px' }}>Material:&nbsp;</span>
                                                                    </td>
                                                                    <td style={{ padding: 0, border: 0, paddingTop: '4px' }}>
                                                                        <Select
                                                                            styles={CompactSelectStyles}
                                                                            name="adjustmentMaterialDropdown"
                                                                            hideClear
                                                                            options={materials}
                                                                            value={(materials ?? []).find((x) => x.value === j.adjustment.materialId) ?? ''}
                                                                            onChange={(selection, event) => {
                                                                                j.adjustment.materialId = selection.value;
                                                                                j.adjustment.materialName = selection.label;

                                                                                this.setState({ dailyEntries });
                                                                            }}
                                                                        />
                                                                    </td>
                                                                </tr>

                                                                <tr>
                                                                    <td style={{ padding: 0, border: 0, paddingTop: '6px' }} align="right">
                                                                        <span style={{ fontSize: '12px' }}>Loads:&nbsp;</span>
                                                                    </td>
                                                                    <td style={{ padding: 0, border: 0, paddingTop: '4px' }}>
                                                                        <NumericInput
                                                                            className="form-control-sm"
                                                                            name="loads"
                                                                            required
                                                                            value={j.adjustment?.loads > 0 ? j.adjustment.loads : 0 }
                                                                            type="number"
                                                                            preventNegative
                                                                            onChange={(event) => {
                                                                                const val = event.target.value;
                                                                                j.adjustment.loads = val;

                                                                                this.setState({ dailyEntries });
                                                                            }}
                                                                        />
                                                                    </td>
                                                                </tr>
                                                            </table>
                                                        }
                                                        
                                                    </td>
                                                    <td>
                                                        <FormGroup className="mb-0">
                                                            <Input
                                                                required
                                                                type="textarea"
                                                                name="notes"
                                                                value={j.adjustment.notes}
                                                                onChange={(evt) => {
                                                                    const { value } = evt.target;
                                                                    j.adjustment.notes = value;
                                                                    this.setState({ dailyEntries });
                                                                }}
                                                                validationMessage="Notes are required."
                                                            />
                                                        </FormGroup>
                                                    </td>
                                                    <td>
                                                        <TimeEntry
                                                            disabled={j.adjustment.chargeTypeObj?.unitsId != 1}
                                                            name="start"
                                                            value={
                                                                j.adjustment.start
                                                            }
                                                            increment={0.083}
                                                            onChange={(ev) => this.onAdjustmentTimeEntryChanged(dailyEntries, j, ev)}
                                                            validationMessage="Start is required."
                                                        />
                                                    </td>
                                                    <td>
                                                        <TimeEntry
                                                            disabled={j.adjustment.chargeTypeObj?.unitsId != 1}
                                                            name="end"
                                                            value={
                                                                j.adjustment.end
                                                            }
                                                            increment={0.083}
                                                            onChange={(ev) => this.onAdjustmentTimeEntryChanged(dailyEntries, j, ev)}
                                                            validationMessage="End is required."
                                                        />
                                                    </td>
                                                    <td>
                                                        <NumericInput
                                                            className="form-control-sm text-right"
                                                            name="quantity"
                                                            required
                                                            value={j.adjustment.quantity}
                                                            type="number"
                                                            preventNegative
                                                            onChange={(event) => {
                                                                const val = event.target.value;
                                                                j.adjustment.quantity = val;

                                                                this.setState({ dailyEntries });
                                                            }}
                                                        />
                                                    </td>
                                                    <td align="center">
                                                        {j.adjustment.chargeTypeObj?.units.description ?? 'Hours'}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                <FlexCenterRow>

                                    {allTSSubmitted && !allTSApproved && !isHourlyAdjusting &&
                                        <>
                                            <Button
                                                className="mb-2"
                                                size="sm"
                                                type="button"
                                                color="secondary"
                                                onClick={() => {
                                                    this.onAdjustClick(j, 1);
                                                }}
                                            >
                                                <i className="fa fa-pen-square fa-lg mr-2" />
                                                Add Adjustment
                                            </Button>

                                            &nbsp;&nbsp;

                                            <Button
                                                disabled={!!saving}
                                                className="mb-2"
                                                size="sm"
                                                color="success"
                                                onClick={() => {
                                                    this.approveHourlyTimesheets(j.scaleTickets[0].linkedTimesheetId);
                                                }}
                                            >
                                                Approve
                                            </Button>
                                        </>
                                    }

                                    {isHourlyAdjusting &&
                                        <>
                                            <Button
                                                disabled={!!saving}
                                                className="mb-2"
                                                size="sm"
                                                color="success"
                                                onClick={() => {
                                                    this.saveAdjustment(j, false);
                                                }}
                                            >
                                                Save
                                            </Button>

                                            &nbsp;&nbsp;

                                            <Button
                                                disabled={!!saving}
                                                className="mb-2"
                                                size="sm"
                                                color="secondary"
                                                onClick={() => {
                                                    this.cancelAdjustment(dailyEntries, j);
                                                }}
                                            >
                                                Cancel
                                            </Button>
                                        </>
                                    }
                                </FlexCenterRow>
                            </CollapseUnderlineHeader>
                        );
                    })}  
                   
                    {jobs.map((j, idx) => {

                        var allTSSubmitted = false;
                        var allTSApproved = false;
                        var isTonnageAdjusting = j.adjusting && j.adjustingUnitsType == 3;

                        const tonnageJobs = (j.scaleTickets ?? []).filter((x) => x.isTonnage == true); 
                        var tonnageJobTotal = this.tonnageChargesTotal(j);

                        if (tonnageJobs.length == 0) return (
                            <span></span>
                        );


                        var materials = [];
                        for (var m = 0; m < j.jobMaterials.length; m++) {
                            materials.push({ label: j.jobMaterials[m].description, value: j.jobMaterials[m].id })
                        }

                        return (
                            <CollapseUnderlineHeader
                                className="timesheet-details-collapse"
                                headerText={j.customer + ' - Tonnage'}
                                isOpen={true}
                            >

                                <div className="details table-responsive">
                                    <table className="table table-sm table-bordered table-striped timesheet-details-table">
                                        <thead className="text-muted">
                                            <tr>
                                                <th className="isadjusted" />
                                                <th className="detailscharge">Job / Haul #</th>
                                                <th className="detailscharge">Ticket #</th>
                                                <th className="detailscharge">Charge</th>
                                                <th className="detailsnote">Resource</th>
                                                <th className="detailsnote">Material</th>
                                                <th className="detailsnote">Notes</th>
                                                <th className="detailsstart">Start</th>
                                                <th className="detailsend">End</th>
                                                <th className="detailsend">Quantity</th>
                                                <th className="detailsunits">Units</th>
                                                <th className="detailsunits">Status</th>
                                                <th className="detailsunits"></th>
                                                <th className="detailsunits"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {tonnageJobs.map((s, idx) => {

                                                allTSSubmitted = s.charges.length > 0
                                                    && s.charges.filter((x) => x.status == 'Unsubmitted').length == 0;

                                                allTSApproved = (s.charges ?? []).filter((x) => x.status == 'Approved').length == (s.charges ?? []).length;

                                                var isTonnage = s.charges.filter((x) => x.chargeTypeUnitsId == 3).length > 0;

                                                return (
                                                    s.charges.map((c, idx) => (
                                                        <tr key={c.id}
                                                            className={`timesheet-details-table-row${c.removed ? ' strikeout' : ''}`}>
                                                           
                                                            <td className="isadjusted">
                                                                {!!c.isAdjustment && (
                                                                    <Badge className="p-1">
                                                                        ADJ
                                                                    </Badge>
                                                                )}
                                                            </td>
                                                          
                                                            <td><span className="site-link" onClick={() => this.onTimesheetEdit(j, s)}>{s.timesheetNumber}</span></td>
                                                            <td>{s.workOrderNumber}</td>
                                                            <td>{c.chargeName}</td>
                                                            <td>{c.resourceName}</td>
                                                            <td>{c.materialName}</td>
                                                            <td>
                                                                {!!c.notes && (
                                                                    <div className="userNote">
                                                                        <div className="noteContent">
                                                                            {
                                                                                c.notes
                                                                            }
                                                                        </div>
                                                                        <div className="noteContentByLine">
                                                                            {`${c.createdBy}, ${moment(c.createdOn).fromNow()}`}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td align="right">{c.chargeTypeUnitsId == 1 && c.start != null ? moment(c.start).format('h:mm a') : ''}</td>
                                                            <td align="right">{c.chargeTypeUnitsId == 1 && c.end != null ? moment(c.end).format('h:mm a') : ''}</td>
                                                            <td align="right">{isTonnage && c.chargeTypeUnitsId == 1  ? '' : c.value}</td>
                                                            <td align="center">{c.chargeTypeUnitsId == 1 ? 'Hours' : 'Tonnage'}</td>

                                                            {c.status == 'Submitted' &&
                                                                <td align="center" style={{ color: 'blue' }}>{c.status}</td>
                                                            }

                                                            {c.status == 'Unsubmitted' &&
                                                                <td align="center" style={{ color: 'red' }}>{c.status}</td>
                                                            }

                                                            {c.status != 'Unsubmitted' && c.status != 'Submitted' &&
                                                                <td align="center">{c.status}</td>
                                                            }
                                                            <td className="detailsdelete">
                                                                {c.status == 'Submitted' && (
                                                                    
                                                                    <FontAwesomeIcon
                                                                        className={`details-edit-button mt-1 cursor-pointer${c.removed ? ' edit' : ''}`}
                                                                        title="edit this charge"
                                                                        icon={faPencil}
                                                                        onClick={() => this.editCharge(j, s, c, 3)}
                                                                    />
                                                                )}
                                                            </td>
                                                            <td className="detailsdelete">
                                                                {c.status == 'Submitted' && (
                                                                    <FontAwesomeIcon
                                                                        className={`details-remove-button text-danger mt-1 cursor-pointer${c.removed ? ' removed' : ''}`}
                                                                        title="Strike this charge"
                                                                        icon={faTrash}
                                                                        onClick={() => this.removeCharge(c.timesheetDetailId)}
                                                                    />
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))
                                                )
                                            })}

                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td colspan="6">&nbsp;</td>
                                                <td align="right"><b>Total</b></td>
                                                <td align="right"><b>{tonnageJobTotal.toFixed(2)}</b></td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>

                                {isTonnageAdjusting && (
                                    <div className="details">
                                        <small className="text-warning font-weight-bold">
                                            ADD ADJUSTMENTS
                                        </small>
                                        <table className="table table-sm table-bordered table-striped adjustment-table">
                                            <thead className="text-muted">
                                                <tr>
                                                    <th className="detailscharge">
                                                        Job / Haul #
                                                    </th>
                                                    <th className="detailscharge">
                                                        Charge
                                                    </th>
                                                    <th className="detailsname">
                                                        Resource
                                                    </th>
                                                    <th className="detailsnote">
                                                        Notes
                                                    </th>
                                                    <th className="detailsstart">
                                                        Start
                                                    </th>
                                                    <th className="detailsend">
                                                        End
                                                    </th>
                                                    <th className="detailsqty">
                                                        Quantity
                                                    </th>
                                                    <th className="detailsunits">
                                                        Units
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>
                                                        <FormGroup className="mb-0">
                                                            <ValidatedSelect
                                                                styles={CompactSelectStyles}
                                                                minMenuHeight={100}
                                                                maxMenuHeight={200}
                                                                name="adjustmentTimesheetNumberDropdown"
                                                                required
                                                                hideClear
                                                                options={haulNumbers}
                                                                getOptionLabel={(option) => option.description}
                                                                getOptionValue={(option) => option.id}
                                                                value={(haulNumbers ?? []).find((x) => x.id === j.adjustment.timesheetId) ?? ''}
                                                                onChange={(selection, event) => {
                                                                    j.adjustment.timesheetId = selection.id;
                                                                    this.setState({ dailyEntries });
                                                                }}
                                                                validationMessage="Haul # is required."
                                                            />
                                                        </FormGroup>
                                                    </td>
                                                    <td>
                                                        <FormGroup className="mb-0">
                                                            <ValidatedSelect
                                                                styles={CompactSelectStyles}
                                                                minMenuHeight={100}
                                                                maxMenuHeight={200}
                                                                name="adjustmentChargeTypeDropdown"
                                                                required
                                                                hideClear
                                                                options={chargeTypes}
                                                                getOptionLabel={(option) => option.description}
                                                                getOptionValue={(option) => option.id}
                                                                value={(chargeTypes ?? []).find((x) => x.id === j.adjustment.chargeTypeId) ?? ''}
                                                                onChange={(selection, event) => {
                                                                    j.adjustment.chargeTypeId = selection.id;
                                                                    j.adjustment.chargeTypeName = selection.description;
                                                                    j.adjustment.chargeTypeObj = selection;
                                                                    j.adjustment.resourceId = employeeId;
                                                                    j.adjustment.resourceName = employeeName;
                                                                    this.setState({ dailyEntries });
                                                                }}
                                                                validationMessage="Charge type is required."
                                                            />
                                                        </FormGroup>
                                                    </td>
                                                    <td>
                                                        <FormGroup className="mb-0">

                                                            {j.adjustment.chargeTypeObj?.resourceTypeId == 1 &&
                                                                <div style={{ marginTop: '5px' }}>{employeeName}</div>
                                                            }

                                                            {j.adjustment.chargeTypeObj?.resourceTypeId == 2 &&
                                                                <ValidatedSelect
                                                                    styles={CompactSelectStyles}
                                                                    minMenuHeight={100}
                                                                    maxMenuHeight={200}
                                                                    name="adjustmentResourceDropdown"
                                                                    required
                                                                    hideClear
                                                                    options={equipment}
                                                                    getOptionLabel={(option) => option.description}
                                                                    getOptionValue={(option) => option.id}
                                                                    value={(equipment ?? []).find((x) => x.id === j.adjustment.resourceId) ?? ''}
                                                                    onChange={(selection, event) => {
                                                                        j.adjustment.resourceId = selection.id;
                                                                        j.adjustment.resourceName = selection.description;

                                                                        this.setState({ dailyEntries });
                                                                    }}
                                                                    validationMessage="Equipment is required."
                                                                />
                                                            }

                                                        </FormGroup>

                                                        {j.adjustment.chargeTypeObj?.resourceTypeId == 2 &&
                                                            <table width="100%">
                                                                <tr style={{ backgroundColor: 'transparent' }}>
                                                                    <td style={{ padding: 0, border: 0, paddingTop: '9px' }} align="right">
                                                                        <span style={{ fontSize: '12px' }}>Material:&nbsp;</span>
                                                                    </td>
                                                                    <td style={{ padding: 0, border: 0, paddingTop: '4px' }}>
                                                                        <Select
                                                                            styles={CompactSelectStyles}
                                                                            name="adjustmentMaterialDropdown"
                                                                            hideClear
                                                                            options={materials}
                                                                            value={(materials ?? []).find((x) => x.value === j.adjustment.materialId) ?? ''}
                                                                            onChange={(selection, event) => {
                                                                                j.adjustment.materialId = selection.value;
                                                                                j.adjustment.materialName = selection.label;

                                                                                this.setState({ dailyEntries });
                                                                            }}
                                                                        />
                                                                    </td>
                                                                </tr>
                                                            </table>
                                                        }
                                                    </td>
                                                    <td>
                                                        <FormGroup className="mb-0">
                                                            <Input
                                                                required
                                                                type="textarea"
                                                                name="notes"
                                                                value={j.adjustment.notes}
                                                                onChange={(evt) => {
                                                                    const { value } = evt.target;
                                                                    j.adjustment.notes = value;
                                                                    this.setState({ dailyEntries });
                                                                }}
                                                                validationMessage="Notes are required."
                                                            />
                                                        </FormGroup>
                                                    </td>
                                                    <td>
                                                        <TimeEntry
                                                            disabled={j.adjustment.chargeTypeObj?.unitsId != 1}
                                                            name="start"
                                                            value={
                                                                j.adjustment.start
                                                            }
                                                            increment={0.083}
                                                            onChange={(ev) => this.onAdjustmentTimeEntryChanged(dailyEntries, j, ev)}
                                                            validationMessage="Start is required."
                                                        />
                                                    </td>
                                                    <td>
                                                        <TimeEntry
                                                            disabled={j.adjustment.chargeTypeObj?.unitsId != 1}
                                                            name="end"
                                                            value={
                                                                j.adjustment.end
                                                            }
                                                            increment={0.083}
                                                            onChange={(ev) => this.onAdjustmentTimeEntryChanged(dailyEntries, j, ev)}
                                                        />
                                                    </td>
                                                    <td>
                                                        <NumericInput
                                                            className="form-control-sm text-right"
                                                            name="quantity"
                                                            value={j.adjustment.quantity ?? 0}
                                                            type="number"
                                                            preventNegative
                                                            onChange={(event) => {
                                                                const val = event.target.value;
                                                                j.adjustment.quantity = val;

                                                                this.setState({ dailyEntries });
                                                            }}
                                                        />
                                                    </td>
                                                    <td align="center">
                                                        {j.adjustment.chargeTypeObj?.units.description ?? 'Hours'}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                <FlexCenterRow>

                                    {allTSSubmitted && !allTSApproved && !isTonnageAdjusting &&
                                        <>
                                            <Button
                                                className="mb-2"
                                                size="sm"
                                                type="button"
                                                color="secondary"
                                                onClick={() => {
                                                    this.onAdjustClick(j, 3);
                                                }}
                                            >
                                                <i className="fa fa-pen-square fa-lg mr-2" />
                                                Add Adjustment
                                            </Button>

                                            &nbsp;&nbsp;

                                            <Button
                                                disabled={!!saving}
                                                className="mb-2"
                                                size="sm"
                                                color="success"
                                                onClick={() => {
                                                    this.approveTonnageTimesheets(j.scaleTickets[0].linkedTimesheetId);
                                                }}
                                            >
                                                Approve
                                            </Button>
                                        </>
                                    }

                                    {isTonnageAdjusting &&
                                        <>
                                            <Button
                                                disabled={!!saving}
                                                className="mb-2"
                                                size="sm"
                                                color="success"
                                                onClick={() => {
                                                    this.saveAdjustment(j, true);
                                                }}
                                            >
                                                Save
                                            </Button>

                                            &nbsp;&nbsp;

                                            <Button
                                                disabled={!!saving}
                                                className="mb-2"
                                                size="sm"
                                                color="secondary"
                                                onClick={() => {
                                                    this.cancelAdjustment(dailyEntries, j);
                                                }}
                                            >
                                                Cancel
                                            </Button>
                                        </>
                                    }
                                </FlexCenterRow>
                            </CollapseUnderlineHeader>
                        );
                    })}    


                    
                </SlideForm>

                <TimesheetEdit
                    id="timeshetEdit"
                    ref={this.timesheetEditRef}
                />

            </>
        );
    }
}
